import { computed, ref, toValue } from 'vue';
import { isProductionEnvironment, usePerspectivesExtensionDetection } from './usePerspectivesExtensionDetection';

const STORAGE_KEY_PREFIX = 'bn_perspectives_setup_dismissed_';
const DISMISS_DURATION_MS = 3 * 24 * 60 * 60 * 1000;

// Survives a remount within the same page load, so a dismissal still holds when localStorage is
// unavailable (private window, blocked site data) or when there's no user id to scope a key with.
const sessionDismissals = new Set();

function storageKey(userId) {
    return userId ? STORAGE_KEY_PREFIX + userId : null;
}

function readDismissedAt(key) {
    if (!key) return null;
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const dismissedAt = Number(raw);
        if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) return null;
        // A record dated in the future would make (now - dismissedAt) negative, which reads as
        // "dismissed moments ago" forever. Treat a clock skew or a hand-edited value as never
        // dismissed rather than silencing the reminder indefinitely.
        if (dismissedAt > Date.now()) return null;
        return dismissedAt;
    } catch {
        return null;
    }
}

function writeDismissedAt(key, dismissedAt) {
    if (!key) return;
    try {
        localStorage.setItem(key, String(dismissedAt));
    } catch {
        // Storage is unavailable - the in-memory record keeps the dismissal for this session.
    }
}

export function usePerspectivesSetupReminder({ hasAccess, userId, isProduction = isProductionEnvironment() } = {}) {
    const resolvedUserId = toValue(userId);
    const key = storageKey(resolvedUserId);
    const sessionKey = String(resolvedUserId ?? '');

    // Detection is shared with the app-bar install button (#5823 follow-up) so the two apply the
    // same polling logic to whether the extension is present, and so this dialog inherits the same
    // production gate: the marker this relies on is only ever set in production, so without that
    // gate this would nag every dev/QA account forever, mistaking "can't be installed here" for
    // "not installed".
    const { hasExtension, stop } = usePerspectivesExtensionDetection({ hasAccess, isProduction });
    const dismissed = ref(false);

    const dismiss = () => {
        stop();
        dismissed.value = true;
        sessionDismissals.add(sessionKey);
        writeDismissedAt(key, Date.now());
    };

    // Without the permission, or outside production, there is nothing to set up — don't touch
    // storage. Access is read once, at mount: permissions only change at login, so a user granted
    // Perspectives mid-session sees the reminder after their next sign-in, which the setup steps
    // ask them to do anyway.
    if (!isProduction || !toValue(hasAccess)) {
        // The dialog never opens here, so there is nothing to dismiss — a no-op keeps a stray call
        // from writing a dismissal record for a reminder that was never shown.
        return { shouldShow: computed(() => false), dismiss: () => {}, stop };
    }

    const dismissedAt = readDismissedAt(key);
    const dismissalStillHolds = dismissedAt !== null && Date.now() - dismissedAt < DISMISS_DURATION_MS;
    dismissed.value = sessionDismissals.has(sessionKey) || dismissalStillHolds;

    const shouldShow = computed(() => !dismissed.value && !hasExtension.value);

    return { shouldShow, dismiss, stop };
}
